import {
  removeNull,
  resetAttribute,
  getDistinct,
  lockRowActions,
  fillBidProjectCode,
  stripFormerNameSuffix,
  applyEntryTeams,
  asRows,
  annotateMultiEntryPersons
} from '@/utils/handleData.js'
import { buildFileList, buildSrcList } from '@/utils/signedDoc'

function parseJsonObject(value) {
  if (!value) return {}
  if (typeof value === 'object' && !Array.isArray(value)) return value
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : {}
    } catch (e) {
      return {}
    }
  }
  return {}
}

function firstText(obj, keys) {
  if (!obj) return ''
  for (let i = 0; i < keys.length; i++) {
    const value = obj[keys[i]]
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim()
    }
  }
  return ''
}

function matchLeaderOption(list, name, phone) {
  const rows = Array.isArray(list) ? list : []
  if (phone) {
    const byPhone = rows.find((item) => {
      return (
        String(item.phoneNumber || item.phone_number || '') === String(phone)
      )
    })
    if (byPhone) return byPhone
  }
  if (!name) return null
  return (
    rows.find((item) => (item.label || item.personName || '') === name) || null
  )
}

function toLeaderOption(item) {
  if (!item) return null
  const value =
    item.value || item.personUid || item.personuid || item.uid || item.leaderUid
  if (!value) return null
  return {
    label:
      item.label ||
      item.personName ||
      item.personname ||
      item.name ||
      item.leaderName ||
      '',
    value: String(value),
    phoneNumber:
      item.phoneNumber ||
      item.phone_number ||
      item.phone ||
      item.telphoneNum ||
      item.leaderPhone ||
      '',
    idcard: item.idcard || item.idCard || item.id_card || '',
    image: item.image || ''
  }
}

function mergeLeaderOptions(list) {
  const options = []
  const seen = {}
  ;(list || []).forEach((item) => {
    const option = toLeaderOption(item)
    if (!option || seen[option.value]) return
    seen[option.value] = true
    options.push(option)
  })
  return options
}

function filterLeaderOptions(list, query) {
  const q = String(query || '')
    .trim()
    .toLowerCase()
  if (!q) return list || []
  return (list || []).filter((item) => {
    const label = String(item.label || '').toLowerCase()
    const idcard = String(item.idcard || '')
    const phone = String(item.phoneNumber || '')
    return label.indexOf(q) > -1 || idcard.indexOf(q) > -1 || phone.indexOf(q) > -1
  })
}

const LEADER_UID_KEYS = [
  'leaderUid',
  'personUid',
  'leaderuid',
  'personuid',
  'leader_uid',
  'person_uid'
]
const LEADER_NAME_KEYS = [
  'leaderName',
  'personName',
  'leadername',
  'personname',
  'leader_name',
  'person_name'
]
const LEADER_PHONE_KEYS = [
  'leaderPhone',
  'leaderphone',
  'phoneNumber',
  'phone_number',
  'telphoneNum',
  'phone'
]

export default {
  namespaced: true,
  state: {
    laborCompanyList: {}, // 缓存劳务公司列表当前页
    laborCompanyInfo: {}, // 缓存劳务公司进退场信息
    manageCompanyList: {}, // 缓存管理单位列表当前页
    manageCompanyInfo: {}, // 缓存管理单位进退场信息
    teamNames: [], // 当前默认或选择劳务公司下属班组
    laborPersonNames: [], // 劳务公司下劳务人员
    managePersonNames: [], // 项目标段下所有管理人员
    leaderSelectOptions: [], // 现场负责人：已有人员+已进场人员去重
    leaderSelectCacheKey: '',
    teamForemanOptions: [], // 班组管理：班组长可选人员（默认已进场，搜索时切换为人员库结果）
    noExitName: [],
    // 劳务公司退场：进入页面前写入，避免表单先按默认展示薪酬再闪隐
    // { skip: boolean, noExitCount, cumulativeShouldPay, cumulativeActualPay, cumulativeOwePay, payRate }
    exitSalaryGate: null
  },
  actions: {
    // 当前默认或选择劳务公司下属在场班组
    async getTeamNames({ commit, dispatch }, uid) {
      if (!uid) {
        commit(
          'baseSet',
          { module: 'contractor', key: 'teamNames', value: [] },
          { root: true }
        )
        return []
      }
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/temp/teaminfo',
          data: {
            fields: 'uid,teamName,foremanName,isExit,mark,workContent',
            projectUid: uid
          }
        },
        { root: true }
      )
      const rows = Array.isArray(res && res.data) ? res.data : []
      const onSite = rows.filter((item) => {
        const status = item.isExit || item.isexit || ''
        return !status || status === '在场'
      })
      const teamNames = resetAttribute(onSite, 'teamName', 'uid')
      commit(
        'baseSet',
        { module: 'contractor', key: 'teamNames', value: teamNames },
        { root: true }
      )
      return teamNames
    },
    // 获取当前机构下未退场人员
    getOrgPersonNames({ dispatch }, data) {
      if (data.personType === '管理人员') {
        dispatch('getManagePersonNames', data.orgUid)
      } else if (data.personType === '劳务人员') {
        dispatch('getNoExitName', data.orgUid)
      }
    },
    // 获取劳务公司下所有在场劳务人员
    async getLaborPersonNames({ commit, dispatch }, uid) {
      var laborPersonNames = []
      if (uid) {
        const onSite = await dispatch('getNoExitName', uid)
        if (Array.isArray(onSite) && onSite.length) {
          laborPersonNames = getDistinct(onSite, 'value')
        } else {
          const res = await dispatch(
            'baseRequest',
            {
              method: 'GET',
              url: '/query/team/leadername',
              data: { vProjectUid: uid },
              noErrorMsg: true
            },
            { root: true }
          )
          const raw = asRows(res && res.data)
          raw.forEach((item) => {
            if (!item) return
            item.personName =
              item.personName || item.personname || item.name || ''
            const entryUid = String(
              item.pEntryUid || item.entryUid || item.uid || ''
            ).trim()
            item.personUid = String(
              item.personUid || item.personuid || item.leaderUid || ''
            ).trim()
            if (entryUid) item.pEntryUid = entryUid
          })
          const named = resetAttribute(raw, 'personName', 'personUid')
          annotateMultiEntryPersons(named, {
            nameKey: 'personName',
            personKey: 'personUid',
            entryKey: 'pEntryUid'
          })
          laborPersonNames = getDistinct(named, 'value')
        }
      }
      commit(
        'baseSet',
        {
          module: 'contractor',
          key: 'laborPersonNames',
          value: laborPersonNames
        },
        { root: true }
      )
      return laborPersonNames
    },
    // 获取劳务公司未退场人员数量
    async getNoExitCount({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/query/buildtype/countlabor',
          data: { laborTeamUid: uid }
        },
        { root: true }
      )
      return res && (res.data || res.data === 0) ? res.data : null
    },
    // 获取劳务公司未退场人员名单
    async getNoExitName({ commit, dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/query/buildtype/exitname',
          data: { laborTeamUid: uid }
        },
        { root: true }
      )
      var noExitName = asRows(res && res.data)
      noExitName.forEach((item) => {
        if (!item) return
        item.personName =
          item.personName || item.personname || item.name || item.leaderName
        const entryUid = String(
          item.pEntryUid ||
            item.entryUid ||
            item.entryuid ||
            item.laborUid ||
            item.uid ||
            ''
        ).trim()
        item.personUid = String(
          item.personUid ||
            item.personuid ||
            item.person_uid ||
            item.leaderUid ||
            ''
        ).trim()
        // 勿把进场 uid 当成 personUid；仅在无人员字段时兜底
        if (!item.personUid && entryUid) item.personUid = entryUid
        if (entryUid) {
          item.pEntryUid = entryUid
          item.uid = entryUid
        }
        item.walkinDate =
          item.walkinDate ||
          item.walkin_date ||
          item.orgBeginTime ||
          item.org_begin_time ||
          ''
        item.walkoffDate =
          item.walkoffDate ||
          item.walkoff_date ||
          item.orgEndTime ||
          item.org_end_time ||
          ''
      })
      noExitName = resetAttribute(noExitName, 'personName', 'personUid')
      if (noExitName.length && uid) {
        const teamRes = await dispatch(
          'baseRequest',
          {
            method: 'GET',
            url: '/person/worker/laborteams',
            data: { laborTeamUid: uid },
            noErrorMsg: true
          },
          { root: true }
        )
        applyEntryTeams(
          noExitName,
          asRows(teamRes && teamRes.data)
        )
      }
      // 同人多次进场：下拉按进场段区分（张三¹（05/01–05/05））
      annotateMultiEntryPersons(noExitName, {
        nameKey: 'personName',
        personKey: 'personUid',
        entryKey: 'pEntryUid'
      })
      commit(
        'baseSet',
        {
          module: 'contractor',
          key: 'noExitName',
          value: noExitName
        },
        { root: true }
      )
      return noExitName
    },
    // 获取项目标段下属所有管理人员
    async getManagePersonNames({ commit, dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/query/buildtype/adminname',
          data: { vProjectUid: uid }
        },
        { root: true }
      )
      const rows = resetAttribute(
        asRows(res && res.data),
        'personName',
        'personUid'
      ).map((item) => {
        const phone =
          item.phoneNumber ||
          item.phone_number ||
          item.personPhone ||
          item.phoneNum ||
          item.telphoneNum ||
          ''
        const entryUid = String(
          item.pEntryUid || item.entryUid || item.uid || ''
        ).trim()
        if (entryUid) item.pEntryUid = entryUid
        item.walkinDate =
          item.walkinDate || item.walkin_date || item.orgBeginTime || ''
        item.walkoffDate =
          item.walkoffDate || item.walkoff_date || item.orgEndTime || ''
        return Object.assign({}, item, {
          phoneNumber: phone
        })
      })
      annotateMultiEntryPersons(rows, {
        nameKey: 'personName',
        personKey: 'personUid',
        entryKey: 'pEntryUid'
      })
      const managePersonNames = getDistinct(rows, 'value')
      commit(
        'baseSet',
        {
          module: 'contractor',
          key: 'managePersonNames',
          value: managePersonNames
        },
        { root: true }
      )
      commit(
        'baseSet',
        {
          module: 'contractor',
          key: 'noExitName',
          value: managePersonNames
        },
        { root: true }
      )
      return managePersonNames
    },
    async loadLeaderSelectOptions(
      { commit, dispatch, state, rootState },
      data = {}
    ) {
      const orgUid = data.orgUid || ''
      const personType = data.personType === 'manage' ? 'manage' : 'labor'
      const cacheKey = personType + ':' + orgUid
      let pool = state.leaderSelectOptions || []
      if (data.force || !pool.length || state.leaderSelectCacheKey !== cacheKey) {
        let entered = []
        if (orgUid) {
          entered =
            personType === 'manage'
              ? await dispatch('getManagePersonNames', orgUid)
              : await dispatch('getLaborPersonNames', orgUid)
        }
        let leaderOption = null
        if (data.leaderUid) {
          await dispatch(
            'person/echoPersonOption',
            {
              uid: data.leaderUid,
              name: data.leaderName
            },
            { root: true }
          )
          leaderOption = (rootState.person.personNames || []).find((item) => {
            return item && String(item.value) === String(data.leaderUid)
          })
        }
        if (!leaderOption && (data.leaderUid || data.leaderName)) {
          leaderOption = {
            label: data.leaderName || data.leaderUid,
            value: String(data.leaderUid || ''),
            phoneNumber: data.leaderPhone || ''
          }
        }
        pool = mergeLeaderOptions([leaderOption].concat(entered || []))
        commit(
          'baseSet',
          {
            module: 'contractor',
            key: 'leaderSelectOptions',
            value: pool
          },
          { root: true }
        )
        commit(
          'baseSet',
          {
            module: 'contractor',
            key: 'leaderSelectCacheKey',
            value: cacheKey
          },
          { root: true }
        )
      }
      const personNames = filterLeaderOptions(pool, data.query)
      commit(
        'baseSet',
        { module: 'person', key: 'personNames', value: personNames },
        { root: true }
      )
      return personNames
    },
    // 劳务公司进场
    async addLaborCompany({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/temp/labor',
          data: data,
          msg: true
        },
        { root: true }
      )
      return res
    },
    // 删除劳务公司
    async deleteLaborCompany({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/temp/labor',
          data: { uid: uid },
          msg: '删除劳务公司成功'
        },
        { root: true }
      )
      return res
    },
    // 获取劳务公司列表
    async getLaborCompanyList({ rootState, commit, dispatch }) {
      var queryValues = await dispatch(
        'bidSection/ensureDefaultBidQuery',
        { queryKey: 'laborCompany', fieldKey: 'upVproUid' },
        { root: true }
      )
      queryValues = Object.assign({}, queryValues)
      queryValues.page = rootState.page.laborCompany
      queryValues.limit = rootState.limit.laborCompany
      queryValues = removeNull(queryValues)
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/query/buildtype/laborcompany',
          data: queryValues
        },
        { root: true }
      )
      var laborCompanyList = res.data ? res.data : {}
      const isSuperAdmin = rootState.user.userName === rootState.superAdminName
      laborCompanyList.result = laborCompanyList.result || []
      laborCompanyList.result.forEach((item) => {
        item.disabledButtons = []
        if (!item.walkoffDate) {
          item.walkoffDate = '未退场'
        }
        if (item.currentStatus === '1') {
          item.disabledButtons = ['', '', '', 3, '']
          item.walkoffDate = '退场确认中'
        } else {
          lockRowActions(
            item,
            item.projectUid !== rootState.user.orgValue ||
              item.noteInfo === '退场',
            isSuperAdmin
          )
        }
        item.companyName = stripFormerNameSuffix(item.companyName)
        item.personName = stripFormerNameSuffix(item.personName)
        item.leaderUid = item.leaderUid || item.personUid || item.leaderuid || ''
        item.leaderName = item.leaderName || item.personName || ''
        item.formerName = item.formerName || item.companyFormerName || ''
        item.personFormerName = item.personFormerName || ''
        const bids = rootState.bidSection.subBidSection || []
        fillBidProjectCode(item, bids)
        if (!item.picUid) {
          item.isPic = false
          item.picStatus = '预览承诺书'
          item.srcList = []
        } else {
          item.isPic = true
          item.picStatus = '承诺书图片'
          item.srcList = buildSrcList({ state: rootState }, item.picUid)
        }
      })
      commit(
        'baseSet',
        {
          module: 'contractor',
          key: 'laborCompanyList',
          value: laborCompanyList
        },
        { root: true }
      )
    },
    // 获取劳务公司进退场信息
    async getLaborCompanyInfo({ rootState, commit, dispatch }, uid) {
      let res = {}
      try {
        res =
          (await dispatch(
            'baseRequest',
            {
              method: 'GET',
              url: '/udf/laborcompany/detailqcode',
              data: { orgUid: uid },
              noErrorMsg: true
            },
            { root: true }
          )) || {}
      } catch (e) {
        res = {}
      }
      // 接口无数据时，回退列表行，避免详情页整页空白
      const rowFallback =
        (rootState.currentRow && rootState.currentRow.laborCompany) || {}
      const laborCompanyInfo = Object.assign(
        {},
        rowFallback,
        res.data ? res.data : {}
      )
      laborCompanyInfo.uid = uid || laborCompanyInfo.vProjectUid || laborCompanyInfo.uid
      laborCompanyInfo.name =
        laborCompanyInfo.laborTeamName ||
        laborCompanyInfo.name ||
        laborCompanyInfo.companyName ||
        ''
      laborCompanyInfo.companyUid =
        laborCompanyInfo.companyUid ||
        laborCompanyInfo.company_uid ||
        rowFallback.companyUid ||
        ''
      laborCompanyInfo.projectName =
        laborCompanyInfo.projectName ||
        laborCompanyInfo.projectCode ||
        rowFallback.projectCode ||
        rowFallback.projectName ||
        ''
      laborCompanyInfo.workContent =
        laborCompanyInfo.workContent || rowFallback.workContent || ''
      const attribute = parseJsonObject(laborCompanyInfo.attribute)
      laborCompanyInfo.leaderUid = firstText(
        laborCompanyInfo,
        LEADER_UID_KEYS
      ) || firstText(attribute, LEADER_UID_KEYS) || rowFallback.leaderUid || rowFallback.personUid || ''
      laborCompanyInfo.leaderName =
        stripFormerNameSuffix(
          firstText(laborCompanyInfo, LEADER_NAME_KEYS) ||
            firstText(attribute, LEADER_NAME_KEYS) ||
            rowFallback.personName ||
            rowFallback.leaderName ||
            ''
        ) || ''
      laborCompanyInfo.leaderPhone =
        firstText(laborCompanyInfo, LEADER_PHONE_KEYS) ||
        firstText(attribute, LEADER_PHONE_KEYS) ||
        rowFallback.phoneNumber ||
        rowFallback.leaderPhone ||
        ''
      if (!laborCompanyInfo.leaderUid && laborCompanyInfo.leaderName) {
        const options = await dispatch(
          'person/searchPersonLibrary',
          laborCompanyInfo.leaderName,
          { root: true }
        )
        const matched = matchLeaderOption(
          options,
          laborCompanyInfo.leaderName,
          laborCompanyInfo.leaderPhone
        )
        if (matched) {
          laborCompanyInfo.leaderUid = matched.value
          laborCompanyInfo.leaderName =
            matched.label || laborCompanyInfo.leaderName
          if (!laborCompanyInfo.leaderPhone) {
            laborCompanyInfo.leaderPhone = matched.phoneNumber || ''
          }
        }
      }
      await dispatch('loadLeaderSelectOptions', {
        orgUid: uid,
        personType: 'labor',
        leaderUid: laborCompanyInfo.leaderUid,
        leaderName: laborCompanyInfo.leaderName,
        leaderPhone: laborCompanyInfo.leaderPhone,
        query: '',
        force: true
      })
      laborCompanyInfo.walkinDate = laborCompanyInfo.orgBeginTime
      laborCompanyInfo.projectUid =
        laborCompanyInfo.projectUid ||
        laborCompanyInfo.upVproUid ||
        laborCompanyInfo.parentUid ||
        (rootState.currentRow &&
          rootState.currentRow.laborCompany &&
          rootState.currentRow.laborCompany.projectUid) ||
        (rootState.formValues &&
          rootState.formValues.laborCompany &&
          rootState.formValues.laborCompany.projectUid) ||
        ''
      laborCompanyInfo.walkoffDate = laborCompanyInfo.orgEndTime
        ? laborCompanyInfo.orgEndTime
        : ''
      laborCompanyInfo.walkoffCause = laborCompanyInfo.walkoffCause
        ? laborCompanyInfo.walkoffCause
        : ''
      laborCompanyInfo.fileLists = {}
      laborCompanyInfo.fileLists.picUid = []
      laborCompanyInfo.fileLists.leaderSignatureUid = []
      laborCompanyInfo.leaderSignatureUid =
        laborCompanyInfo.leaderSignatureUid ||
        laborCompanyInfo.leadersignatureuid ||
        ''
      if (laborCompanyInfo.leaderSignatureUid) {
        commit(
          'pictureSet',
          {
            key: 'leaderSignatureUid',
            value: [laborCompanyInfo.leaderSignatureUid],
            type: 'signature'
          },
          { root: true }
        )
        laborCompanyInfo.fileLists.leaderSignatureUid =
          rootState.pictures.leaderSignatureUid || []
      }
      if (laborCompanyInfo.picUid) {
        laborCompanyInfo.fileLists.picUid = buildFileList(
          { state: rootState },
          laborCompanyInfo.picUid,
          (laborCompanyInfo.name || '') + '退场承诺书'
        )
        laborCompanyInfo.srcList = buildSrcList(
          { state: rootState },
          laborCompanyInfo.picUid
        )
      }
      laborCompanyInfo.qcodeInfo = {
        qcodeUrl: laborCompanyInfo.qcode || uid,
        qcodeName: laborCompanyInfo.name,
        project: laborCompanyInfo.projectName,
        leftText: '用诚信企业',
        rightText: '干品质工程'
      }
      commit(
        'baseSet',
        {
          module: 'contractor',
          key: 'laborCompanyInfo',
          value: laborCompanyInfo
        },
        { root: true }
      )
      return laborCompanyInfo
    },
    // 获取劳务公司退场登记信息
    async getLaborCompanyExitInfo({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        { method: 'GET', url: '/query/buildtype/walkoff', data: data },
        { root: true }
      )
      return res && res.data ? res.data : []
    },
    // 编辑劳务公司信息
    async editLaborCompany({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/temp/editlabor',
          data: data,
          msg: '编辑劳务公司成功'
        },
        { root: true }
      )
      return res
    },
    // 劳务公司退场
    async exitLaborCompany({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/temp/labor',
          data: data,
          msg: '劳务公司退场成功'
        },
        { root: true }
      )
      return res
    },
    // 上传退场承诺书图片uid
    async uploadLaborCompanyoutpic({ dispatch }, data) {
      const payload = Object.assign({}, data || {})
      const silent = payload.notify === false
      delete payload.notify
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/temp/laboroutpic',
          data: payload,
          msg: silent ? undefined : '上传退场承诺书图片成功'
        },
        { root: true }
      )
      return res
    },
    // 管理单位进场
    async addManageCompany({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/temp/admincompin',
          data: data,
          msg: '管理单位进场成功'
        },
        { root: true }
      )
      return res
    },
    // 删除管理单位
    async deleteManageCompany({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/temp/admincompremove',
          data: { uid: uid },
          msg: '删除管理单位成功'
        },
        { root: true }
      )
      return res
    },
    // 获取管理单位列表
    async getManageCompanyList({ rootState, commit, dispatch }) {
      var queryValues = await dispatch(
        'bidSection/ensureDefaultBidQuery',
        { queryKey: 'manageCompany', fieldKey: 'upVproUid' },
        { root: true }
      )
      queryValues = Object.assign({}, queryValues)
      queryValues.page = rootState.page.manageCompany
      queryValues.limit = rootState.limit.manageCompany
      queryValues = removeNull(queryValues)
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/query/buildtype/admincompany',
          data: queryValues
        },
        { root: true }
      )
      var manageCompanyList = res.data ? res.data : {}
      const isSuperAdmin = rootState.user.userName === rootState.superAdminName
      manageCompanyList.result = manageCompanyList.result || []
      manageCompanyList.result.forEach((item) => {
        item.disabledButtons = []
        lockRowActions(
          item,
          item.orgUid !== rootState.user.orgValue || item.noteInfo === '退场',
          isSuperAdmin
        )
        item.uid = item.orgUid
        item.orgName = stripFormerNameSuffix(item.orgName)
        item.name = item.orgName
        item.formerName = item.formerName || item.companyFormerName || ''
        item.leaderName = stripFormerNameSuffix(item.leaderName)
        const bids = rootState.bidSection.subBidSection || []
        fillBidProjectCode(item, bids)
        if (item.projectCode) {
          item.orgCode = item.projectCode
        }
      })
      commit(
        'baseSet',
        {
          module: 'contractor',
          key: 'manageCompanyList',
          value: manageCompanyList
        },
        { root: true }
      )
    },
    // 获取管理单位进退场信息
    async getManageCompanyInfo({ rootState, commit, dispatch }, uid) {
      let res = {}
      try {
        res =
          (await dispatch(
            'baseRequest',
            {
              method: 'GET',
              url: '/udf/company/detailqcode',
              data: { orgUid: uid },
              noErrorMsg: true
            },
            { root: true }
          )) || {}
      } catch (e) {
        res = {}
      }
      var manageCompanyInfo = res.data ? res.data : {}
      manageCompanyInfo.uid = uid
      manageCompanyInfo.name = manageCompanyInfo.orgName
      manageCompanyInfo.leaderUid =
        manageCompanyInfo.leaderUid || manageCompanyInfo.personUid || ''
      manageCompanyInfo.leaderName =
        manageCompanyInfo.leaderName || manageCompanyInfo.personName || ''
      manageCompanyInfo.buildType = manageCompanyInfo.orgType
      manageCompanyInfo.walkinDate =
        manageCompanyInfo.walkinDate ||
        manageCompanyInfo.orgBeginTime ||
        ''
      manageCompanyInfo.projectUid =
        manageCompanyInfo.projectUid ||
        manageCompanyInfo.orgUid ||
        uid ||
        (rootState.currentRow &&
          rootState.currentRow.manageCompany &&
          (rootState.currentRow.manageCompany.projectUid ||
            rootState.currentRow.manageCompany.orgUid)) ||
        ''
      manageCompanyInfo.walkOutDate = manageCompanyInfo.walkoffDate
      manageCompanyInfo.walkoffCause = manageCompanyInfo.walkoffCause
        ? manageCompanyInfo.walkoffCause
        : ''
      manageCompanyInfo.qcodeInfo = {
        qcodeUrl: manageCompanyInfo.qcode || uid,
        qcodeName: manageCompanyInfo.orgName,
        project: manageCompanyInfo.orgCode,
        leftText: '用诚信企业',
        rightText: '干品质工程'
      }
      await dispatch('loadLeaderSelectOptions', {
        orgUid: uid,
        personType: 'manage',
        leaderUid: manageCompanyInfo.leaderUid,
        leaderName: manageCompanyInfo.leaderName,
        leaderPhone: manageCompanyInfo.leaderPhone || '',
        query: '',
        force: true
      })
      commit(
        'baseSet',
        {
          module: 'contractor',
          key: 'manageCompanyInfo',
          value: manageCompanyInfo
        },
        { root: true }
      )
      return manageCompanyInfo
    },
    // 编辑管理单位信息
    async editManageCompany({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/temp/projectinfoedit',
          data: data,
          msg: '编辑管理单位成功'
        },
        { root: true }
      )
      return res
    },
    // 管理单位退场
    async exitManageCompany({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/temp/admincompout',
          data: data,
          msg: '管理单位退场成功'
        },
        { root: true }
      )
      return res
    }
  }
}
